import React, { useState, useEffect } from "react";
import styled from "styled-components";
import FullButton from "../components/Buttons/FullButton";
import {
  Container,
  Form,
  FormWrapper,
  Input,
  Message,
  PageWrapper,
  StatusMessage,
  Wrapper,
} from "../style/view.styles";
import { PasswordInput } from "../components/Inputs/PasswordInput";
import { useNavigate, useLocation } from "react-router-dom";

export default function ResetPassword() {
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [formData, setFormData] = useState({
    otp: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from location state (passed from forgot password)
    if (location.state && location.state.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }

    // Set up countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [location]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { otp, password, confirmPassword } = formData;

    if (!otp) {
      setFormStatus({
        message: "Please enter the OTP code",
        type: "error",
      });
      return;
    }

    if (password !== confirmPassword) {
      setFormStatus({
        message: "Passwords do not match!",
        type: "error",
      });
      return;
    }

    setLoading(true);
    setFormStatus({
      message: "",
      type: "success",
    });

    try {
      const appUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${appUrl}/reset/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: otp,
          password: password,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFormStatus({
          message: "Password reset successful! Redirecting to login...",
          type: "success",
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setFormStatus({
          message: data.result || "Failed to reset password. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      setFormStatus({
        message: "Something went wrong. Please try again later.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    navigate("/request-reset-password");
  };

  return (
    <>
      <Wrapper className="paleBlueGreyBg">
        <Container>
          <PageWrapper className="container flexCenter">
            <FormWrapper>
              <h1 className="extraBold font40 darkGreenColor">Reset Password</h1>
              <p className="font16" style={{ textAlign: "center", margin: "20px 0" }}>
                Enter the verification code sent to your email and create a new password
              </p>
              <Form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  value={formData.otp}
                  onChange={handleInputChange}
                  required
                />
                
                <PasswordInput
                  type="password"
                  name="password"
                  placeholder="New Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />

                <PasswordInput
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />

                <FullButton title={loading ? "Resetting..." : "Reset Password"} />
                
                <TimerSection>
                  {timeLeft > 0 ? (
                    <div>Code expires in: <span className="bold">{formatTime(timeLeft)}</span></div>
                  ) : (
                    <div>OTP has expired</div>
                  )}
                  <ResendButton 
                    onClick={resendOTP} 
                    disabled={loading}
                    type="button"
                  >
                    Resend OTP
                  </ResendButton>
                </TimerSection>
              </Form>
              {formStatus.message && (
                <StatusMessage type={formStatus.type}>
                  {formStatus.message}
                </StatusMessage>
              )}
              <Message className="font13">
                Remember your password? <a href="/login">Login here</a>
              </Message>
            </FormWrapper>
          </PageWrapper>
        </Container>
      </Wrapper>
    </>
  );
}

const TimerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0;
  font-size: 14px;
  gap: 10px;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.disabled ? "#aaa" : "#49cb86"};
  text-decoration: underline;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  font-size: 14px;
  padding: 5px;
  
  &:hover {
    color: ${props => props.disabled ? "#aaa" : "#3aa66a"};
  }
`;